import { NPCTSnapshot } from "types/testTypes";

/**
 * Build axis ticks/labels for an axis whose underlying values are timestamps
 * but whose visible labels should be D (= 1 / distance) values.
 *
 * The chart still positions data by time, so the data order is preserved.
 * We pick timestamps where D crosses round multiples of `step` (e.g. 5, 10,
 * 15, ...) — once on the way in and once on the way out.
 *
 * User input D values are intentionally not added as ticks here — they're
 * shown on the trigger reference-line labels instead, so they don't collide
 * with the regular target ticks.
 *
 * Returns:
 *   - `ticks`:  an array of timestamps to feed into the axis `ticks` prop.
 *   - `labels`: a Map from timestamp to the formatted D label that the
 *               axis `tickFormatter` should render.
 */
export function computeDValueTicks(
  snapshots: NPCTSnapshot[],
  step = 5,
): { ticks: number[]; labels: Map<number, string> } {
  const valid = snapshots.filter((s) => s.distance > 0);
  if (valid.length === 0) {
    return { ticks: [], labels: new Map() };
  }

  // Split the trajectory at its peak D (closest target) so we can sample
  // the ascending and descending phases independently.
  const peakIdx = valid.reduce(
    (best, s, i) => (1 / s.distance > 1 / valid[best].distance ? i : best),
    0,
  );
  const ascending = valid.slice(0, peakIdx + 1);
  const descending = valid.slice(peakIdx);

  const labels = new Map<number, string>();
  const claimed = new Set<number>();

  // Find the snapshot in `phase` whose D is closest to `target` and within
  // step/2 of it; skip snapshots already claimed by an earlier tick.
  const findClosestUnclaimed = (phase: NPCTSnapshot[], target: number) => {
    let best: NPCTSnapshot | null = null;
    let bestDiff = step / 2;
    for (const s of phase) {
      if (claimed.has(s.timestamp)) continue;
      const diff = Math.abs(1 / s.distance - target);
      if (diff <= bestDiff) {
        best = s;
        bestDiff = diff;
      }
    }
    return best;
  };

  const maxD = 1 / Math.min(...valid.map((s) => s.distance));

  // Iterate from `step` upward — D=0 is never physically reached so we
  // don't try to plant a tick there.
  for (let target = step; target <= maxD + step / 2; target += step) {
    const ascSnap = findClosestUnclaimed(ascending, target);
    if (ascSnap) {
      labels.set(ascSnap.timestamp, formatTargetD(1 / ascSnap.distance));
      claimed.add(ascSnap.timestamp);
    }
    const descSnap = findClosestUnclaimed(descending, target);
    if (descSnap) {
      labels.set(descSnap.timestamp, formatTargetD(1 / descSnap.distance));
      claimed.add(descSnap.timestamp);
    }
  }

  const filteredLabels = thinByMinSpacing(
    labels,
    valid[valid.length - 1].timestamp - valid[0].timestamp,
  );
  const ticks = Array.from(filteredLabels.keys()).sort((a, b) => a - b);
  return { ticks, labels: filteredLabels };
}

// Drop ticks that crowd a neighbour. Higher-D ticks win ties so the peak
// (which is the most informative landmark) is always preserved; the rest
// of the timeline fills in greedily around it. The threshold is a fraction
// of the test's total duration, so it scales to whatever timeframe the
// chart covers.
const MIN_TICK_SPACING_FRACTION = 0.06;

function thinByMinSpacing(labels: Map<number, string>, totalRange: number): Map<number, string> {
  if (labels.size === 0 || totalRange <= 0) return labels;
  const minSpacing = totalRange * MIN_TICK_SPACING_FRACTION;

  // Sort by D value descending — peak first, then lower D values fill in
  // wherever they're not crowded out by an already-placed neighbour.
  const entries = Array.from(labels.entries()).sort((a, b) => parseFloat(b[1]) - parseFloat(a[1]));

  const placedTimestamps: number[] = [];
  const result = new Map<number, string>();
  for (const [ts, label] of entries) {
    const tooClose = placedTimestamps.some((p) => Math.abs(p - ts) < minSpacing);
    if (!tooClose) {
      placedTimestamps.push(ts);
      result.set(ts, label);
    }
  }
  return result;
}

/** Format a user-input D value for display on a trigger reference line. */
export function formatUserInputD(distanceM: number): string {
  return (1 / distanceM).toFixed(1);
}

// Sampled snapshots typically land within ~0.1 of the target D, so rounding
// to an integer gives the clean "5, 10, 15, 20" labels the chart wants while
// still reflecting the actual data.
function formatTargetD(d: number): string {
  return Math.round(d).toString();
}
